import React, { useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'

// Styling
import { applyTextStyle, applyTextEllipsis } from '../../styles/typography'
import { fastTransition, shadows } from '../../styles/effects'

// Components
import { Icon, IconWrapper } from '../icon'
import CircleIcon from '../icon/CircleIcon'
import Text from '../Text'

// Types
import { IFormControlProps } from '../../common/models/IFormControlProps'
import { IconType } from '../icon/IconType'

export interface IProps extends IFormControlProps {
  iconType?: IconType
  iconHoverText?: string
  coloredBackground?: boolean
  bottomCaption?: string
  children?: React.ReactNode
  id?: string
  className?: string
}

const Dropdown = ({ className, ...props }: IProps) => {
  const themeContext = useContext(ThemeContext)
  const errorMessage =
    props.meta && props.meta.error && props.meta.touched
      ? props.meta.error
      : props.errorMessage

  return (
    <DropdownWrapper
      className={className}
      onChange={props.handleChange}
      disabled={props.disabled}
      errorMessage={errorMessage}
    >
      {props.label && (
        <LabelIconWrapper>
          <DropdownLabel
            textStyle="labelTinyCaps"
            color={props.disabled ? 'textLighter' : 'text'}
            marginBottom={props.additionalLabel ? 4 : 8}
          >
            {props.label}
          </DropdownLabel>
          {props.iconType && (
            <CircleIcon
              type={props.iconType}
              size={16}
              color={themeContext.colors.textLight}
              hoverText={props.iconHoverText}
            />
          )}
        </LabelIconWrapper>
      )}
      {props.label && props.additionalLabel && (
        <Text
          textStyle="caption"
          marginBottom={8}
          color={props.disabled ? 'textLighter' : 'textLight'}
        >
          {props.additionalLabel}
        </Text>
      )}
      <DropdownInnerWrapper>
        <StyledDropdown {...props} coloredBackground={props.coloredBackground}>
          {props.children}
        </StyledDropdown>
        <Icon
          type="Dropdown"
          size={12}
          color={themeContext.colors.primaryFadeHalf}
        />
      </DropdownInnerWrapper>
      {props.bottomCaption && (
        <SmallCaption textStyle="captionSmall" color="textLight" marginTop={4}>
          {props.bottomCaption}
        </SmallCaption>
      )}
      {errorMessage && (
        <SmallCaption textStyle="captionSmall" color="secondary" marginTop={4}>
          {errorMessage}
        </SmallCaption>
      )}
    </DropdownWrapper>
  )
}

const DropdownLabel = styled(Text)`
  ${applyTextEllipsis()}
`

const DropdownInnerWrapper = styled.div`
  position: relative;

  ${IconWrapper} {
    position: absolute;
    z-index: 4;
    pointer-events: none;
    top: 50%;
    transform: translateY(-50%);
    right: 16px;
    margin-right: 0;
  }
`

const StyledDropdown = styled.select<{ coloredBackground?: boolean }>`
  ${({ theme, coloredBackground }) =>
    `
      border: 1px solid ${
        coloredBackground ? theme.colors.textLightest : theme.colors.textLighter
      };
      border-radius: 8px;
      width: 100%;
      padding: 12px 36px 12px 12px;
      color: ${theme.colors.text};
      background-color: ${
        coloredBackground ? theme.colors.primaryLight : theme.colors.lightest
      };
      transition: border ${fastTransition};
  `}

  position: relative;
  z-index: 4;
  display: block;
  appearance: none;
  outline: 0;
  ${applyTextStyle('bodySmall')};
  box-shadow: ${shadows.depthOne};
  cursor: pointer;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:disabled {
    pointer-events: none;
    color: ${({ theme }) => theme.colors.textLighter};

    & + * svg {
      display: none;
    }
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.text72};
  }

  &:hover,
  &:focus {
    + span > svg > path {
      fill: ${({ theme }) => theme.colors.primary};
    }

    border-color: ${({ theme }) => theme.colors.primary};
  }
`

export const DropdownWrapper = styled.div<
  Pick<IFormControlProps, 'disabled' | 'errorMessage'>
>`
  position: relative;

  ${({ errorMessage, theme }) =>
    errorMessage &&
    css`
      ${StyledDropdown} {
        border-color: ${theme.colors.secondary};

        & + * path {
          fill: ${theme.colors.secondary};
        }
      }
    `}
`

const SmallCaption = styled(Text)`
  padding-left: 1px;
`

const LabelIconWrapper = styled.div`
  display: flex;
  align-items: baseline;
`

export default Dropdown
